/**
 * Client-side helpers for smart filters.
 *
 * A filter is a saved search plus actions, so the *conditions* half needs no
 * code here — it reuses `mailSearchQuery`'s `buildQuery`/`parseQueryFields`, the
 * same pair that drives the advanced-search panel. What lives here is the action
 * half: its shape, how to describe it to a person, and what makes a draft
 * invalid before it's worth sending to the server.
 */

import { EMPTY_FIELDS, buildQuery, parseQueryFields } from './mailSearchQuery';

export const FILTER_CATEGORIES = [
  { value: '', label: "Don't change" },
  { value: 'primary', label: 'Primary' },
  { value: 'social', label: 'Social' },
  { value: 'promotions', label: 'Promotions' },
  { value: 'updates', label: 'Updates' },
  { value: 'forums', label: 'Forums' },
];

export const EMPTY_ACTIONS = Object.freeze({
  addLabels: [],
  markRead: false,
  star: false,
  markImportant: false,
  setCategory: '',
  archive: false,
  markSpam: false,
  trash: false,
  forwardTo: '',
  stopProcessing: false,
});

/**
 * Toggle actions, in the order they appear in the form.
 *
 * `exclusiveWith` records the actions that make another pointless, so the form
 * can grey them out instead of letting the user build a rule where one action
 * silently overrides another.
 */
export const ACTION_TOGGLES = [
  { key: 'markRead', label: 'Mark as read' },
  { key: 'star', label: 'Star it' },
  { key: 'markImportant', label: 'Mark as important' },
  { key: 'archive', label: 'Skip the inbox (archive it)', exclusiveWith: ['trash', 'markSpam'] },
  { key: 'markSpam', label: 'Mark as spam', exclusiveWith: ['trash'] },
  { key: 'trash', label: 'Delete it (move to trash)' },
  { key: 'stopProcessing', label: "Don't run any later filters" },
];

const EMAIL_RE = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/** Blank filter draft: condition form state plus an action set. */
export const emptyDraft = () => ({
  _id: null,
  name: '',
  fields: { ...EMPTY_FIELDS },
  labelsText: '',
  actions: { ...EMPTY_ACTIONS },
});

/** Turn a saved filter from the API back into an editable draft. */
export const draftFromFilter = (filter = {}) => ({
  _id: filter._id || null,
  name: filter.name || '',
  fields: parseQueryFields(filter.query || ''),
  labelsText: (filter.actions?.addLabels || []).join(', '),
  actions: { ...EMPTY_ACTIONS, ...(filter.actions || {}), addLabels: filter.actions?.addLabels || [] },
});

/** Split the label input on commas, trimming and dropping blanks. */
export const parseLabelsText = (text) =>
  String(text || '')
    .split(',')
    .map((label) => label.trim())
    .filter(Boolean);

/** The request body for create/update. */
export const toPayload = (draft) => ({
  name: String(draft.name || '').trim(),
  query: buildQuery(draft.fields),
  actions: {
    ...draft.actions,
    addLabels: parseLabelsText(draft.labelsText),
    forwardTo: String(draft.actions.forwardTo || '').trim().toLowerCase(),
  },
});

/** True when the action set would actually change something. */
export const hasAnyAction = (actions = {}) =>
  Boolean(
    (actions.addLabels || []).length ||
      actions.markRead ||
      actions.star ||
      actions.markImportant ||
      actions.setCategory ||
      actions.archive ||
      actions.markSpam ||
      actions.trash ||
      String(actions.forwardTo || '').trim()
  );

/**
 * Reasons a draft can't be saved yet, phrased for display.
 *
 * Mirrors the server's rules so the user is told what's wrong while they're
 * still in the form; the server remains the authority.
 */
export const validateDraft = (draft, { ownerEmail = '' } = {}) => {
  const errors = [];
  const payload = toPayload(draft);

  if (!payload.query.trim()) {
    errors.push('Add at least one condition, otherwise the filter would match every message.');
  }
  if (!hasAnyAction(payload.actions)) {
    errors.push('Choose at least one action.');
  }

  const forwardTo = payload.actions.forwardTo;
  if (forwardTo) {
    if (!EMAIL_RE.test(forwardTo)) {
      errors.push(`"${forwardTo}" is not a valid email address.`);
    } else if (forwardTo === String(ownerEmail || '').trim().toLowerCase()) {
      errors.push('Forwarding to your own address would loop forever.');
    }
  }

  return errors;
};

const CATEGORY_LABELS = FILTER_CATEGORIES.reduce((map, item) => {
  if (item.value) map[item.value] = item.label;
  return map;
}, {});

/**
 * One-line plain-English summary of what a filter does, for the filter list.
 *
 * Destination is reported once and exclusively, matching how the server resolves
 * it, so a filter set to both archive and delete reads as "Delete" rather than
 * claiming to do both.
 */
export const describeActions = (actions = {}) => {
  const parts = [];
  const labels = actions.addLabels || [];

  if (labels.length) parts.push(`Apply label ${labels.join(', ')}`);
  if (actions.markRead) parts.push('Mark as read');
  if (actions.star) parts.push('Star');
  if (actions.markImportant) parts.push('Mark as important');
  if (actions.setCategory) parts.push(`Categorise as ${CATEGORY_LABELS[actions.setCategory] || actions.setCategory}`);

  if (actions.trash) parts.push('Delete');
  else if (actions.markSpam) parts.push('Mark as spam');
  else if (actions.archive) parts.push('Skip the inbox');

  if (actions.forwardTo) parts.push(`Forward to ${actions.forwardTo}`);
  if (actions.stopProcessing) parts.push('stop later filters');

  return parts.join(' · ') || 'Does nothing';
};

/**
 * Actions that a higher-priority action in the same set makes irrelevant, so the
 * form can disable them.
 */
export const suppressedActions = (actions = {}) => {
  const suppressed = new Set();
  ACTION_TOGGLES.forEach(({ key, exclusiveWith }) => {
    if (!exclusiveWith) return;
    if (exclusiveWith.some((other) => actions[other])) suppressed.add(key);
  });
  return suppressed;
};
